import type {
    Maybe,
    WorkOrderActKc2PriceData,
    WorkOrderSeasonPriceData,
    FilterValueOfWorkOrderPriceCodeFilterData,
} from 'gql/graphql';
import { formatNumberToRu, formatNumberToRuF } from 'utils';

import * as R from 'ramda';

export const getPlanVolumeTermsRepresentation = (planVolumeTerms: Maybe<any[]> | undefined) => {
    if (!planVolumeTerms || planVolumeTerms.length === 1) {
        return null;
    }

    return planVolumeTerms
        .map((v, i) => (v > 0 ? `${i ? ' + ' : ''}${v}` : `${i ? ' - ' : '-'}${Math.abs(v)}`))
        ?.join('');
};

const flatSeasons = (item: any) => ({
    ...item,
    ...item?.seasonPrices?.reduce((memo: any, value: WorkOrderSeasonPriceData) => {
        memo[getSeasonVolumeKey(value.seasonId)] = formatNumberToRu(value.planVolume, 3);
        memo[getSeasonPlanVolumeTermsKey(value.seasonId)] = getPlanVolumeTermsRepresentation(value.planVolumeTerms);
        memo[getSeasonCostKey(value.seasonId)] = value.planCost;
        memo[getSeasonCompletedVolumeKey(value.seasonId)] = formatNumberToRu(value.completedVolume, 3);
        memo[getSeasonCompletedCostKey(value.seasonId)] = value.completedCost;
        memo[getSeasonReminderVolumeKey(value.seasonId)] = formatNumberToRu(value.remainderVolume, 3);
        memo[getSeasonReminderCostKey(value.seasonId)] = value.remainderCost;

        return memo;
    }, {}),
});

const flatActs = (item: any) => ({
    ...item,
    ...item?.actKC2Prices?.reduce((memo: any, value: WorkOrderActKc2PriceData) => {
        memo[`actKC2planVolume_${value.actKC2Id}`] = formatNumberToRu(value.planVolume, 3);
        memo[`actKC2planCost_${value.actKC2Id}`] = value.planCost === 0 ? '0' : value.planCost;
        memo[`actKC2acceptedVolume_${value.actKC2Id}`] = formatNumberToRu(value.acceptedVolume, 3);
        memo[`actKC2acceptedCost_${value.actKC2Id}`] = value.acceptedCost === 0 ? '0' : value.acceptedCost;
        memo[`actKC2acceptedVolumeDeviation_${value.actKC2Id}`] = value.acceptedVolumeDeviation;
        memo[`actKC2planVolumeDeviation_${value.actKC2Id}`] = value.planVolumeDeviation;
        memo[`actKC2isReadOnly_${value.actKC2Id}`] = value.isReadOnly;
        memo[`actKC2isEditableCost_${value.actKC2Id}`] = value?.isEditableCost;

        return memo;
    }, {}),
});

export const workOrderItemFormatter = R.pipe(
    flatSeasons,
    flatActs,
    R.evolve({
        totalPlanVolume: formatNumberToRuF(3),
        totalCompletedVolume: formatNumberToRuF(3),
        totalRemainderVolume: formatNumberToRuF(3),
    }),
);

// @ts-ignore
export const sortLinkedActs = (array: any[]): any[] => R.pipe(R.sortBy(R.prop('id')), R.reverse)(array);
export const getSeasonVolumeKey = (id: number) => `seasonPlanVolume_${id}`;
export const getSeasonCostKey = (id: number) => `seasonPlanCost_${id}`;
export const getSeasonPlanVolumeTermsKey = (id: number) => `seasonPlanVolumeTerms_${id}`;
export const getSeasonCompletedVolumeKey = (id: number) => `seasonCompletedVolume_${id}`;
export const getSeasonCompletedCostKey = (id: number) => `seasonCompletedCost_${id}`;
export const getSeasonReminderVolumeKey = (id: number) => `seasonReminderVolume_${id}`;
export const getSeasonReminderCostKey = (id: number) => `seasonReminderCost_${id}`;
export const showNotNull = (val: any) => val ?? '';

export const destroyWorkOrderFilters = ({ api, columns }: { api: any; columns: string[] }) => {
    if (!api || !columns) {
        return;
    }

    columns.forEach((filterName) => {
        api.destroyFilter(filterName);
    });
};

type TFilterNames = 'code' | 'status' | 'unit' | 'name';
export const getWorkOrderWhereFromFilter = (filterName: TFilterNames, values: any[]) => {
    if (!values?.length) {
        if (filterName === 'code') {
            return { or: { [filterName]: undefined } };
        }

        return { [filterName]: undefined };
    }

    if (filterName === 'code') {
        return {
            or: values.map(({ uerCode, code, isUer }: { uerCode: string; code: string; isUer: boolean }) => ({
                and: [
                    { uerCode: { eq: uerCode } },
                    {
                        or: [{ code: { eq: isUer ? uerCode : code } }, { code: { eq: uerCode } }],
                    },
                ],
            })),
        };
    }

    if (filterName === 'status') {
        return {
            [filterName]: {
                in: values,
            },
        };
    }

    return {
        [filterName]: {
            or: values.map((item: string) => ({
                eq: item,
            })),
        },
    };
};

export const workOrderCodeFilterGroupHelper = (items: FilterValueOfWorkOrderPriceCodeFilterData[]) => {
    if (!items) {
        return undefined;
    }

    const groupedData =
        items?.reduce(
            (
                acc: {
                    [key: string]: FilterValueOfWorkOrderPriceCodeFilterData[];
                },
                item,
            ) => {
                if (item?.value) {
                    if (!acc[item?.value?.uerCode]) {
                        acc[item?.value?.uerCode] = [item];
                    } else if (item?.value?.isUer) {
                        acc[item?.value?.uerCode].unshift(item);
                    } else {
                        acc[item?.value?.uerCode].push(item);
                    }
                }

                return acc;
            },
            {},
        ) ?? {};

    return Object.values(groupedData).flat();
};
